import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { messagingAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import {
    Sparkles, CheckCircle2, ArrowRight, Clock, AlertTriangle,
    TrendingDown, Phone, Users, FileText, Home, Briefcase,
    ChevronDown, ChevronUp, MessageSquare
} from 'lucide-react';

const CATEGORY_CONFIG = {
    baisse_prix: { icon: TrendingDown, label: 'Baisse de prix', color: 'text-red-400', bg: 'bg-red-500/10' },
    relance_acquereur: { icon: Phone, label: 'Relance acquéreur', color: 'text-orange-400', bg: 'bg-orange-500/10' },
    suivi_vendeur: { icon: Users, label: 'Suivi vendeur', color: 'text-blue-400', bg: 'bg-blue-500/10' },
    rappel_telephonique: { icon: Phone, label: 'Rappel', color: 'text-purple-400', bg: 'bg-purple-500/10' },
    suivi_prix: { icon: TrendingDown, label: 'Suivi prix', color: 'text-amber-400', bg: 'bg-amber-500/10' },
    prospection: { icon: Briefcase, label: 'Prospection', color: 'text-green-400', bg: 'bg-green-500/10' },
    documents: { icon: FileText, label: 'Documents', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
    visite: { icon: Home, label: 'Visite', color: 'text-teal-400', bg: 'bg-teal-500/10' },
    administratif: { icon: FileText, label: 'Administratif', color: 'text-slate-400', bg: 'bg-slate-500/10' },
    information: { icon: MessageSquare, label: 'Info', color: 'text-blue-400', bg: 'bg-blue-500/10' },
    probleme: { icon: AlertTriangle, label: 'Problème', color: 'text-red-400', bg: 'bg-red-500/10' },
};

const PRIORITY_STYLES = {
    haute: { dot: 'bg-red-500', border: 'border-l-red-500', badge: 'bg-red-500/15 text-red-400' },
    moyenne: { dot: 'bg-orange-500', border: 'border-l-orange-500', badge: 'bg-orange-500/15 text-orange-400' },
    basse: { dot: 'bg-blue-500', border: 'border-l-blue-500', badge: 'bg-blue-500/15 text-blue-400' },
};

export default function AiActionsWidget({ basePath }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(true);
    const [completing, setCompleting] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await messagingAPI.getDashboardActions();
            setData(res.data);
            if (res.data.has_new && res.data.total > 0) {
                toast.info(`${res.data.total} action(s) en attente`, {
                    id: 'ai-actions-pending',
                    description: 'Des nouvelles directives ont été extraites de la messagerie',
                    icon: <Sparkles className="w-4 h-4 text-[#FF914D]" />,
                    duration: 5000,
                });
            }
        } catch { /* ignore */ }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleComplete = async (id) => {
        setCompleting(id);
        try {
            await messagingAPI.completeAiReminder(id);
            setData(prev => ({
                ...prev,
                actions: prev.actions.filter(a => a.id !== id),
                total: prev.total - 1,
            }));
            toast.success('Action terminée');
        } catch {
            toast.error('Erreur');
        }
        setCompleting(null);
    };

    const [showAll, setShowAll] = useState(false);

    if (loading) {
        return (
            <div className="bg-card rounded-2xl border border-border p-4 animate-pulse">
                <div className="h-6 bg-muted rounded w-48 mb-3" />
                <div className="space-y-2">
                    <div className="h-14 bg-muted rounded-xl" />
                    <div className="h-14 bg-muted rounded-xl" />
                </div>
            </div>
        );
    }

    if (!data || data.total === 0) return null;

    const actions = data.actions || [];

    return (
        <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden" data-testid="ai-actions-widget">
            {/* Header */}
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                data-testid="ai-actions-header"
            >
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                        <Sparkles className="w-4.5 h-4.5 text-white" />
                    </div>
                    <div className="text-left">
                        <h3 className="font-bold text-sm flex items-center gap-2">
                            Actions du jour
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#FF914D]/15 text-[#FF914D] font-bold">
                                {data.total}
                            </span>
                        </h3>
                        <p className="text-[10px] text-muted-foreground">
                            {data.by_priority.haute > 0 && <span className="text-red-400 font-bold">{data.by_priority.haute} urgente(s)</span>}
                            {data.by_priority.haute > 0 && data.by_priority.moyenne > 0 && ' · '}
                            {data.by_priority.moyenne > 0 && <span className="text-orange-400">{data.by_priority.moyenne} moyenne(s)</span>}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to={`${basePath}/messaging`}
                        onClick={e => e.stopPropagation()}
                        className="text-xs text-muted-foreground hover:text-[#FF914D] flex items-center gap-1 transition-colors"
                    >
                        Messagerie <ArrowRight className="w-3 h-3" />
                    </Link>
                    {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </div>
            </button>

            {/* Actions list */}
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 space-y-2">
                            {actions.slice(0, showAll ? actions.length : 4).map((action, idx) => {
                                const cat = CATEGORY_CONFIG[action.category] || CATEGORY_CONFIG.information;
                                const pri = PRIORITY_STYLES[action.priority] || PRIORITY_STYLES.moyenne;
                                const CatIcon = cat.icon;
                                const isCompleting = completing === action.id;

                                return (
                                    <motion.div
                                        key={action.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 10 }}
                                        transition={{ delay: idx * 0.03 }}
                                        className={cn(
                                            "p-3 rounded-xl border-l-[3px] bg-white/[0.03] group hover:bg-white/[0.06] transition-all",
                                            pri.border
                                        )}
                                        data-testid={`action-${action.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", cat.bg)}>
                                                <CatIcon className={cn("w-4 h-4", cat.color)} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="font-semibold text-sm text-foreground leading-tight">{action.title}</p>
                                                    <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 uppercase", pri.badge)}>
                                                        {action.priority}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">{action.description}</p>
                                                {action.assigned_by_name && (
                                                    <p className="text-[10px] text-[#FF914D] mt-1 flex items-center gap-1">
                                                        <Users className="w-3 h-3" /> De : {action.assigned_by_name}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {/* Quick action bar — always visible, tap-friendly */}
                                        <div className="flex items-center gap-2 mt-2.5 pl-11">
                                            {(action.category === 'suivi_vendeur' || action.category === 'rappel_telephonique' || action.category === 'relance_acquereur' || /contacter|appeler|relancer|rappel/i.test(action.title)) && (
                                                <a
                                                    href={action.contact_phone ? `tel:${action.contact_phone}` : 'tel:'}
                                                    onClick={e => e.stopPropagation()}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/10 text-green-400 text-xs font-medium hover:bg-green-500/20 transition-colors"
                                                    data-testid={`action-call-${action.id}`}
                                                >
                                                    <Phone className="w-3 h-3" /> {action.contact_name ? `Appeler ${action.contact_name.split(' ')[0]}` : 'Appeler'}
                                                </a>
                                            )}
                                            {action.mandate_id && (
                                                <Link
                                                    to={`${basePath}/mandates/v2/${action.mandate_id}`}
                                                    onClick={e => e.stopPropagation()}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#681793]/10 text-[#681793] text-xs font-medium hover:bg-[#681793]/20 transition-colors"
                                                    data-testid={`action-mandate-${action.id}`}
                                                >
                                                    <FileText className="w-3 h-3" /> {action.mandate_number || 'Voir mandat'}
                                                </Link>
                                            )}
                                            <button
                                                onClick={() => handleComplete(action.id)}
                                                disabled={isCompleting}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 text-muted-foreground text-xs font-medium hover:bg-green-500/10 hover:text-green-400 transition-colors ml-auto"
                                                data-testid={`complete-action-${action.id}`}
                                            >
                                                <CheckCircle2 className={cn("w-3.5 h-3.5", isCompleting && "animate-spin")} />
                                                Fait
                                            </button>
                                        </div>
                                    </motion.div>
                                );
                            })}
                            {!showAll && actions.length > 4 && (
                                <button
                                    onClick={() => setShowAll(true)}
                                    className="w-full py-2.5 text-center text-xs font-medium text-[#FF914D] hover:text-white bg-[#FF914D]/5 hover:bg-[#FF914D]/10 rounded-xl transition-colors"
                                    data-testid="show-all-actions"
                                >
                                    Voir les {actions.length - 4} autres actions
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
